import { useRightDrawer } from "@/contexts/RightDrawerContext";
import { useSelectedConversation } from "@/contexts/SelectedConversationContext";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { useResponsive } from "@/hooks/useResponsive";
import { DataRoomCustomFolder } from "@/models/data-room/DataRoomCustomFolder";
import { DataRoomEntryType } from "@/models/data-room/DataRoomEntryType";
import { DataRoomFile } from "@/models/data-room/DataRoomFile";
import { DataRoomRedFlag } from "@/models/data-room/DataRoomRedFlag";
import { DataRoomEntry, DataRoomLazoModalSx } from "@/pages/OneDataRoomPage";
import DataRoomCustomFolderService from "@/services/DataRoomCustomFolderService";
import DataRoomService from "@/services/DataRoomService";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import { ConversationType } from "@/types/ConversationType";
import { removeExtension } from "@/utils/FilenameUtils";
import { AddOutlined } from "@mui/icons-material";
import { Box, Typography, styled } from "@mui/material";
import { RowSelectionState } from "@tanstack/react-table";
import { HttpStatusCode } from "axios";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonButton, { CommonButtonSecondarySx } from "../utils/CommonButton";
import LazoModal from "../utils/LazoModal";
import { ALL_ENTRIES_JOINED, DATA_ROOM_STATIC_ID, findEntryByStaticId } from "./DataRoomCategoriesDefines";
import { TREE_FILTERS } from "./DataRoomDirectoryFileTree";
import DataRoomEmptyManageScreen from "./DataRoomEmptyManageScreen";
import DataRoomFileUploadModal from "./DataRoomFileUploadModal";
import DataRoomFolderFilesTable from "./DataRoomFolderFilesTable";
import DataRoomMasterFilesTable from "./DataRoomMasterFilesTable";
import { DataRoomMoveMenu } from "./DataRoomMoveMenu";
import { DataRoomCountCircle, DataRoomTabContainer } from "./DataRoomRedFlagsSection";
import DataRoomRedFlagsTable from "./DataRoomRedFlagsTable";
import TransferFileMessage from "./TransferFileMessage";
import { DataRoomNotificationModal } from "./common-components/DataRoomNotificationModal";
import { useDataRoomProvider } from "./contexts/DataRoomContext";
import useFileUploadProgress from "./useFileUploadProgress";

enum TabType {
  DOCUMENTS = "Documents",
  RED_FLAGS = "Red Flags",
}

type UndoState = {
  files: DataRoomFile[];
  movedTo: DataRoomCustomFolder | DataRoomEntry | null;
  wasMovedFrom: DataRoomCustomFolder | DataRoomEntry | null;
};

interface DataRoomManageSectionProps {
  files: DataRoomFile[];
  setFiles: Dispatch<SetStateAction<DataRoomFile[]>>;
  selectedFiles: DataRoomFile[];
  setSelectedFiles: Dispatch<SetStateAction<DataRoomFile[]>>;
  loading: boolean;
  setTree?: Dispatch<SetStateAction<DataRoomEntry>>;
  focusedFile?: DataRoomFile;
  setFocusedFile?: Dispatch<SetStateAction<DataRoomFile>>;
}

export default function DataRoomManageSection(props: DataRoomManageSectionProps) {
  const { t } = useTranslation("virtualDataRooms");
  const { isMobile, isTablet } = useResponsive();
  const isMobileOrTablet = isMobile || isTablet;

  const [showTransferFileMessage, setShowTransferFileMessage] = useState<boolean>(true);
  const [moving, setMoving] = useState<boolean>(false);
  const [moveMenuAnchor, setMoveMenuAnchor] = useState<null | HTMLElement>(null);
  const openMoveMenu = Boolean(moveMenuAnchor);

  const [openFileUploadModal, setOpenFileUploadModal] = useState<boolean>(false);

  const [approving, setApproving] = useState<boolean>(false);

  const [tab, setTab] = useState<TabType>(TabType.DOCUMENTS);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [openNotificationModal, setOpenNotificationModal] = useState<boolean>(false);

  const [lastMovedState, setLastMovedState] = useState<UndoState>({ files: [], wasMovedFrom: null, movedTo: null });

  const { currentTenantMembership } = useTenantMemberships();
  const { selected: selectedEntry, selectedCustom, treeFilter } = useDataRoomProvider();

  useEffect(() => {
    let f: DataRoomFile[] = [];
    Object.keys(rowSelection).forEach((idx) => f.push(props.files[idx]));
    props.setSelectedFiles(f);
  }, [rowSelection]);

  // Just call backend when we want to update the left tree count, its easier
  const fetchTreeStats = useCallback(() => {
    if (!props.setTree) return;
    // Optimistic
    DataRoomService.getStats(currentTenantMembership.tenant.externalId, ALL_ENTRIES_JOINED).then((r) => {
      r.onSuccess((c) => {
        props.setTree?.(c);
      });
    });
  }, [currentTenantMembership.tenant.externalId]);

  async function handleMoveFiles(selectedMove: DataRoomEntry | DataRoomCustomFolder, type: DataRoomEntryType) {
    if (selectedMove === null) return;
    if (props.selectedFiles.length === 0) {
      NotificationService.warn(t("dataRoomFiles.manageSection.notifications.noFilesSelected"));
      return;
    }
    setMoving(true);
    const fileExternalIds: string[] = props.selectedFiles.map((f) => f.externalId);

    let entry: DataRoomCustomFolder | DataRoomEntry = findEntryByStaticId(props.selectedFiles[0].parentStaticId);
    if (treeFilter === TREE_FILTERS.CUSTOM) {
      entry = selectedCustom;
    }

    if (type === DataRoomEntryType.STATIC) {
      const casted = selectedMove as DataRoomEntry;

      const r = await DataRoomService.moveFiles(fileExternalIds, casted.staticId, entry["externalId"]);
      r.onSuccess((_) => {
        setLastMovedState({ files: props.selectedFiles, wasMovedFrom: entry, movedTo: selectedMove });
        props.setFiles((prev) => prev.filter((f) => !fileExternalIds.includes(f.externalId)));
        fetchTreeStats();
        setRowSelection({});
        setOpenNotificationModal(true);
      });

      r.notifyErrorIfPresent();
    } else if (type === DataRoomEntryType.CUSTOM) {
      const casted = selectedMove as DataRoomCustomFolder;

      const r = await DataRoomCustomFolderService.moveFiles(fileExternalIds, casted.externalId, selectedCustom?.externalId);

      r.onSuccess((_) => {
        setLastMovedState({ files: props.selectedFiles, wasMovedFrom: entry, movedTo: selectedMove });
        props.setFiles((prev) => prev.filter((f) => !fileExternalIds.includes(f.externalId)));
        fetchTreeStats();
        setRowSelection({});
        setOpenNotificationModal(true);
      }).notifyErrorIfPresent();
    }

    setMoving(false);
  }

  const undoMoveFilesAction = async () => {
    if (!lastMovedState.files || !lastMovedState.wasMovedFrom) return;

    if (Boolean(lastMovedState.wasMovedFrom["staticId"])) {
      const response = await DataRoomService.moveFiles(
        lastMovedState.files.map((f) => f.externalId),
        lastMovedState.wasMovedFrom["staticId"]
      );
      response
        .onSuccess(() => {
          fetchTreeStats();
          props.setFiles(lastMovedState.files);
          handleOnCloseDataRoomNotificationModal();
        })
        .onError((message, _) => {
          NotificationService.error(t("dataRoomFiles.manageSection.notifications.errorMovingFiles"));
          console.error(`Error: ${message}`);
          fetchTreeStats();
          handleOnCloseDataRoomNotificationModal();
        });
    } else if (Boolean(lastMovedState.wasMovedFrom["externalId"])) {
      const response = await DataRoomCustomFolderService.moveFiles(
        lastMovedState.files.map((f) => f.externalId),
        lastMovedState.wasMovedFrom["externalId"]
      );

      response.onSuccess(() => {
        fetchTreeStats();
        props.setFiles(lastMovedState.files);
        handleOnCloseDataRoomNotificationModal();
      });

      response.onError((message, _) => {
        NotificationService.error(t("dataRoomFiles.manageSection.notifications.errorMovingFiles"));
        console.error(`Error: ${message}`);
        fetchTreeStats();
        handleOnCloseDataRoomNotificationModal();
      });
    }
  };

  function handleMoveMenuClick(ev: React.MouseEvent<HTMLElement, MouseEvent>) {
    if (!moveMenuAnchor) {
      setMoveMenuAnchor(ev.currentTarget);
    } else {
      setMoveMenuAnchor(null);
    }
  }

  // Handle file upload when NOT in Master
  const handleFileUpload = (files: File[]) => {
    if (selectedCustom) {
      DataRoomCustomFolderService.uploadFiles(files, currentTenantMembership.tenant.externalId, selectedCustom.externalId).then((r) => {
        r.onSuccess((c) => {
          setOpenFileUploadModal(false);
          props.setFiles((prev) => [...prev, ...c]);
        }).notifyErrorIfPresent();
      });
    } else if (selectedEntry) {
      DataRoomService.uploadFiles(files, currentTenantMembership.tenant.externalId, selectedEntry.staticId).then((r) => {
        r.onSuccess((c) => {
          setOpenFileUploadModal(false);
          props.setFiles((prev) => [...prev, ...c]);
          fetchTreeStats();
        }).onError((e) => {
          NotificationService.error(t("dataRoomFiles.manageSection.notifications.errorUploadingFile"));
          console.log("error: " + e);
        });
      });
    }
  };

  const { files, setFiles, uploadedFiles, fileProgressMap, resetFileProgressMap } = useFileUploadProgress();

  function handleApproveClick(): void {
    if (!props.selectedFiles || props.selectedFiles.length === 0) {
      NotificationService.warn(t("dataRoomFiles.manageSection.notifications.selectFileToApprove"));
      return;
    }

    const externalIds = props.selectedFiles.map((f) => f.externalId);

    setApproving(true);
    DataRoomService.approveFiles(externalIds)
      .then((r) => {
        r.onSuccess(() => {
          NotificationService.info(t("dataRoomFiles.manageSection.notifications.filesMovedToSuggested"));
          props.setFiles((prev) => prev.filter((f) => !externalIds.includes(f.externalId)));
          setRowSelection({});
          fetchTreeStats();
        }).onError((e) => {
          NotificationService.error(t("dataRoomFiles.manageSection.notifications.couldNotMoveFiles"));
          console.log("Error: " + e);
        });
      })
      .finally(() => setApproving(false));
  }

  const [flagCount, setFlagCount] = useState<number>(0);
  useEffect(() => {
    setFlagCount(
      props.files.reduce((acc, f) => {
        acc += f.redFlags.length;
        return acc;
      }, 0)
    );
  }, [props.files]);

  function handleFlagsUpdate(update: DataRoomRedFlag[]) {
    props.setFiles((prev) => {
      return prev.map((f) => {
        f.redFlags = update.filter((u) => u.file?.externalId === f.externalId);
        return f;
      });
    });
  }

  const isMaster = treeFilter === TREE_FILTERS.CUSTOM ? !selectedCustom : selectedEntry?.staticId === DATA_ROOM_STATIC_ID.MASTER;

  const handleOnClickDelete = async () => {
    setDeleting(true);
    const fExternalIds = props.selectedFiles.map((f) => f.externalId);
    DataRoomService.deleteFiles(fExternalIds, treeFilter === TREE_FILTERS.CUSTOM && selectedCustom?.externalId)
      .then((r) => {
        r.onSuccess((_) => {
          NotificationService.info(t("dataRoomFiles.manageSection.notifications.filesDeleted"));
          props.setFiles((prev) => prev.filter((f) => !fExternalIds.includes(f.externalId)));
          props.setSelectedFiles([]);
          setRowSelection({});
        }).onError((e) => {
          NotificationService.error(t("dataRoomFiles.manageSection.notifications.errorDeletingFiles"));
          console.log("error: " + e);
        });
      })
      .finally(() => {
        setDeleting(false);
        setOpenConfirmDeleteModal(false);
      });
  };

  const handleOnCloseDataRoomNotificationModal = () => {
    setLastMovedState({ files: [], wasMovedFrom: null, movedTo: null });
    setOpenNotificationModal(false);
  };

  const { setIsOpen: setIsOpenRightDrawer } = useRightDrawer();
  const { setSelectedConversation } = useSelectedConversation();
  function onAskAI(file: DataRoomFile) {
    let convTitle = t("dataRoomFiles.manageSection.ai.conversationAbout") + " " + removeExtension(file.name);

    setIsOpenRightDrawer(true);
    LazoChatConversationService.createSingleDocumentConversation({
      title: convTitle,
      document: file,
      tenantExternalId: currentTenantMembership.tenant.externalId,
      conversationType: ConversationType.SINGLE_DOCUMENT_DATA_ROOM,
    }).then((r) => {
      r.onSuccess((c) => setSelectedConversation(c)).onError((e) => {
        if (r.getHttpStatusCode() === HttpStatusCode.BadRequest) {
          NotificationService.warn(e);
        } else {
          NotificationService.error(e);
        }
      });
    });
  }

  return (
    <>
      <ManageBarContainer>
        {isMaster && (
          <Box
            sx={{
              display: "flex",
              padding: "1rem",
              width: "100%",
              borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: "0.5rem", sm: "1rem" },
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <Box
              display="flex"
              height={{ xs: "auto", sm: "1.75rem" }}
              alignItems="center"
              justifyContent="space-between"
              gap="1rem"
              width={"100%"}
              sx={{
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              <>
                <Box
                  display="flex"
                  alignItems="center"
                  gap="1rem"
                  sx={{
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <Typography
                    className="title"
                    sx={{
                      textAlign: { xs: "center", sm: "left" },
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    {t("dataRoomFiles.manageSection.title")}
                    {props.files?.length > 0 && `(${props.files.length})`}
                  </Typography>
                  {props.selectedFiles?.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        gap: "0.5rem",
                        flexDirection: { xs: "column", sm: "row" },
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      <CommonButton
                        disabled={approving || !props.selectedFiles || props.selectedFiles.length === 0}
                        onClick={handleApproveClick}
                        sx={{
                          ...CommonButtonSecondarySx,
                          height: { xs: "2.5rem", sm: "1.75rem" },
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        }}
                      >
                        {approving ? (
                          <Spinner size="1rem" sx={{ color: Colors.LAZO_PURPLE }} />
                        ) : (
                          <Typography sx={{ color: Colors.LAZO_PURPLE, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                            {t("dataRoomFiles.manageSection.buttons.approve")}
                          </Typography>
                        )}
                      </CommonButton>
                      <Box onClick={handleMoveMenuClick}>
                        <CommonButton
                          purpleBorderVariant
                          style={{ ...MoveButtonStyle(openMoveMenu), height: isMobileOrTablet ? "2.5rem" : "1.75rem" }}
                          disabled={props.selectedFiles?.length === 0}
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                          {t("dataRoomFiles.manageSection.buttons.move")}
                        </CommonButton>
                      </Box>
                    </Box>
                  )}
                </Box>
              </>

              {props.selectedFiles?.length > 0 && (
                <CommonButton
                  disabled={props.selectedFiles?.length === 0}
                  onClick={() => setOpenConfirmDeleteModal(true)}
                  sx={{
                    border: `1px solid ${Colors.RED}`,
                    color: Colors.RED,
                    backgroundColor: Colors.WHITE,
                    height: { xs: "2.5rem", sm: "100%" },
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    "&:hover": {
                      backgroundColor: Colors.ACCENT_RED_DESATURATED_FFEAEA,
                    },
                  }}
                >
                  {t("dataRoomFiles.manageSection.buttons.delete")}
                </CommonButton>
              )}
            </Box>
          </Box>
        )}
        {!isMaster && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
              paddingTop: { xs: "0.5rem", md: 0 },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: "1rem", sm: 0 },
            }}
          >
            <Box display="flex" alignItems="center" sx={{ width: { xs: "100%", sm: "auto" } }}>
              <DataRoomTabContainer current={tab === TabType.DOCUMENTS} onClick={() => setTab(TabType.DOCUMENTS)}>
                <Typography className="text" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                  {t("dataRoomFiles.manageSection.tabs.documents")}
                </Typography>
              </DataRoomTabContainer>
              {/* LAT-710 HIDDEN */}
              {false && (
                <DataRoomTabContainer current={tab === TabType.RED_FLAGS} onClick={() => setTab(TabType.RED_FLAGS)}>
                  <Typography className="text" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                    {t("dataRoomFiles.manageSection.tabs.redFlags")}
                  </Typography>
                  {flagCount > 0 && (
                    <DataRoomCountCircle>
                      <Typography>{flagCount}</Typography>
                    </DataRoomCountCircle>
                  )}
                </DataRoomTabContainer>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "end",
                gap: "1rem",
                marginRight: "1rem",
                flexDirection: "row",
                marginY: "0.5rem",
              }}
            >
              {props.selectedFiles?.length > 0 && (
                <Box
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                  }}
                  onClick={handleMoveMenuClick}
                >
                  <CommonButton
                    purpleBorderVariant
                    style={{
                      ...MoveButtonStyle(openMoveMenu),
                      height: isMobileOrTablet ? "2.5rem" : "1.75rem",
                      width: isMobileOrTablet ? "100%" : "auto",
                    }}
                    disabled={props.selectedFiles?.length === 0}
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    {t("dataRoomFiles.manageSection.buttons.move")}
                  </CommonButton>
                </Box>
              )}
              <CommonButton
                purpleBorderVariant
                style={{
                  borderRadius: "0.25rem",
                  gap: "0.25rem",
                  height: isMobileOrTablet ? "2.5rem" : "1.75rem",
                  padding: isMobileOrTablet ? "0 1rem" : "0 0.5rem",
                  width: isMobileOrTablet ? "100%" : "auto",
                }}
                onClick={() => setOpenFileUploadModal(true)}
                sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              >
                <AddOutlined sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }} />
                <Typography fontSize={{ xs: "0.75rem", sm: "0.75rem" }}>
                  {t("dataRoomFiles.manageSection.buttons.uploadToThisFolder")}
                </Typography>
              </CommonButton>
            </Box>
          </Box>
        )}
        <TransferFileMessage setOpen={setShowTransferFileMessage} open={showTransferFileMessage} />
      </ManageBarContainer>
      {props.loading && <Spinner size={"2rem"} style={{ color: Colors.LAZO_PURPLE }} />}
      {!props.loading ? (
        props.files.length === 0 ? (
          <DataRoomEmptyManageScreen onFileUpload={handleFileUpload} />
        ) : isMaster ? (
          <DataRoomMasterFilesTable
            setFiles={props.setFiles}
            currentEntry={selectedEntry}
            setSelected={(f) => props.setSelectedFiles(f)}
            selectedFiles={props.selectedFiles}
            files={props.files}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            onAskAI={onAskAI}
            focusedFile={props.focusedFile}
            setFocusedFile={props.setFocusedFile}
          />
        ) : tab === TabType.DOCUMENTS ? (
          <DataRoomFolderFilesTable
            setFiles={props.setFiles}
            files={props.files}
            selected={props.selectedFiles}
            setSelected={props.setSelectedFiles}
            fetchTreeStats={fetchTreeStats}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            onAskAI={onAskAI}
            focusedFile={props.focusedFile}
            setFocusedFile={props.setFocusedFile}
          />
        ) : (
          <DataRoomRedFlagsTable
            flags={props.files.reduce((p, f) => {
              p.push(...f.redFlags);
              return p;
            }, [])}
            onFlagsUpdate={handleFlagsUpdate}
          />
        )
      ) : (
        <></>
      )}
      {/* File Upload Modal, ONLY WHEN NOT IN MASTER */}
      {!isMaster && openFileUploadModal && (
        <DataRoomFileUploadModal
          files={files}
          setFiles={setFiles}
          uploadedFiles={uploadedFiles}
          uploadFunc={handleFileUpload}
          open={openFileUploadModal}
          setOpen={setOpenFileUploadModal}
          fileProgressMap={fileProgressMap}
          resetFileProgressMap={resetFileProgressMap}
        />
      )}
      {/* Move Menu */}
      <DataRoomMoveMenu anchor={moveMenuAnchor} setAnchor={setMoveMenuAnchor} onMoveFiles={handleMoveFiles} moving={moving} />
      <LazoModal
        sx={DataRoomLazoModalSx}
        open={openConfirmDeleteModal}
        title={t("dataRoomFiles.manageSection.modals.confirmDelete.title")}
        handleClose={() => setOpenConfirmDeleteModal(false)}
        buttons={[
          <CommonButton
            style={{ borderRadius: "0.5rem" }}
            purpleBorderVariant
            onClick={() => setOpenConfirmDeleteModal(false)}
            key={"cancel-button"}
          >
            {t("dataRoomFiles.manageSection.buttons.cancel")}
          </CommonButton>,
          <CommonButton
            style={{
              borderRadius: "0.5rem",
              backgroundColor: Colors.HEART_RED,
            }}
            onClick={handleOnClickDelete}
            disabled={deleting}
            key={"delete-button"}
          >
            {!deleting ? t("dataRoomFiles.manageSection.buttons.delete") : <Spinner size={"1.5rem"} />}
          </CommonButton>,
        ]}
      >
        <Typography fontSize="0.75rem" fontWeight="400" lineHeight="18px" color={Colors.LIGHT_GRAY_8A8E93}>
          {t("dataRoomFiles.manageSection.modals.confirmDelete.message")}
        </Typography>
      </LazoModal>
      {openNotificationModal && (
        <DataRoomNotificationModal
          open={openNotificationModal}
          title={t("dataRoomFiles.manageSection.notifications.filesWereMoved", { count: lastMovedState.files.length })}
          description={t("dataRoomFiles.manageSection.notifications.filesMovedTo", {
            files: lastMovedState.files.map((f) => f.name).join(","),
            count: lastMovedState.files.length,
            destination: lastMovedState.movedTo?.name,
          })}
          handleOnClickClose={handleOnCloseDataRoomNotificationModal}
          handleOnClickUndo={undoMoveFilesAction}
        />
      )}
    </>
  );
}

const ManageBarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMobileOrTablet",
})<{ isMobileOrTablet?: boolean }>(({ theme }) => ({
  height: "fit-content",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  width: "100%",
  "& .title": {
    fontSize: "0.875rem",
    fontWeight: 600,
    lineHeight: "20px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.75rem",
      lineHeight: "18px",
    },
  },
}));

const MoveButtonStyle = (openMoveMenu: boolean) => ({
  height: "1.75rem",
  border: openMoveMenu ? `1px solid ${Colors.BUTTON_PURPLE}` : `1px solid ${Colors.BUTTON_SECONDARY_PURPLE}`,
});
